<script setup>
  import { computed, onMounted, provide, ref, nextTick, watch } from "vue";
  import { useEventBus } from "@vueuse/core";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useSurveyErrorsStore } from "@stores/survey/surveyErrors";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useScrollToCard } from "@utils/composables/useScrollToCard";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import CardsPane from "@/components/Survey/Components/CardsList/CardsPane.vue";
  import CurrentCard from "@/components/Survey/Edit/CurrentCard/CurrentCard.vue";
  import SettingsGeneral from "@/components/Survey/Edit/CurrentCard/Settings/SettingsGeneral.vue";
  import EditCardComments from "@/components/Survey/Review/Collaboration/EditCardComments.vue";
  import RightPanel from "@/components/Survey/Review/Collaboration/RightPanel.vue";

  const surveyCardsStore = useSurveyCardsStore();
  const surveyErrorsStore = useSurveyErrorsStore();
  const { errorsByCard } = storeToRefs(surveyErrorsStore);
  const { presenceOfCard } = storeToRefs(surveyCardsStore);
  const { applyDrag, updateSearchQuery } = surveyCardsStore;
  const {
    addCard,
    addCardAtTheEnd,
    removeCard,
    duplicateCard,
    duplicateCards,
    selectCurrentCard,
    selectFirstCardAsCurrent,
    findCardByToken,
    isSelectableCard,
    selectConditions,
    setStartFromCardToken,
    setActivePane,
  } = surveyCardsStore;
  const currentCardStore = useCurrentCardStore();
  const { currentCard, currentIndex, currentToken, settings, isSettingHidden } = storeToRefs(currentCardStore);
  const { updateCardName, updateCardType } = currentCardStore;

  const props = defineProps({
    survey: { type: Object, required: true },
    questionCount: { type: Number, required: true },
    cards: { type: Array, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
    switchBetweenTabs: { type: Boolean, required: true },
  });

  const route = useRoute();

  const selectCardFromUrl = () => {
    const card = findCardByToken({ token: route.query.card });
    if (!card || !isSelectableCard(card)) return false;
    selectCurrentCard({ token: card.token });
    return true;
  };

  onMounted(() => {
    if (props.switchBetweenTabs || selectCardFromUrl()) scrollToCard();
    else selectFirstCardAsCurrent();
  });

  const playFromCard = ({ token }) => {
    setStartFromCardToken(token);
    setActivePane("Test");
  };

  const editEventBus = useEventBus("edit");
  provide("editEventBus", editEventBus);
  provide("reviewEventBus", editEventBus);

  const reviewStore = useReviewCommentsStore();
  const rightPane = ref("settings");
  const hasSettings = computed(() => Object.keys(settings.value).some((name) => !isSettingHidden.value[name]));
  const rightPanes = computed(() => [hasSettings.value && "settings", "comments"].filter(Boolean));
  watch(
    () => reviewStore.composerFocusAt,
    () => (rightPane.value = "comments"),
  );
  watch(rightPanes, (panes) => {
    if (!panes.includes(rightPane.value)) rightPane.value = panes[0];
  });
  editEventBus.on(async (eventDescriptor) => {
    const { command, ...event } = eventDescriptor;

    const actions = {
      add: addCard,
      addAtTheEnd: addCardAtTheEnd,
      remove: removeCard,
      duplicate: duplicateCard,
      duplicateMany: duplicateCards,
      select: selectCurrentCard,
      changeType: updateCardType,
      changeName: updateCardName,
      applyDrag: applyDrag,
      selectConditions: selectConditions,
      search: updateSearchQuery,
      scrollToFooter: scrollToFooter,
      playFromCard: playFromCard,
    };
    actions[command]?.(event);

    // Scroll to the new card after add/duplicate actions
    if (["add", "addAtTheEnd", "duplicate", "duplicateMany"].includes(command)) {
      await scrollToCard();
    }
  });

  const cardsPane = ref(null);
  const { scrollToCard } = useScrollToCard(cardsPane, currentToken);

  const scrollToFooter = async () => {
    await nextTick();
    const lastCardIndex = props.cards.length - 1;
    const token = props.cards[lastCardIndex].token;
    const targetCard = document.getElementById(`nav-menu-item-${token}`);
    if (targetCard && cardsPane.value?.cardList?.scrollAreaRef) {
      const scrollArea = cardsPane.value.cardList.scrollAreaRef;
      scrollArea.scrollTop = targetCard.offsetTop - 10;
    }
  };
</script>

<template>
  <ThreePanePage
    :span-left="3"
    :span-center="6"
    :span-right="3"
    data-cy="survey-questions-edit-page">
    <template #left>
      <CardsPane
        ref="cardsPane"
        :card-sub-types-infos="cardSubTypesInfos"
        :cards="cards"
        :errors-by-card="errorsByCard"
        event-bus-name="editEventBus"
        :is-card-filtered-out="isCardFilteredOut"
        :is-filter-active="isFilterActive"
        :question-count="questionCount"
        :selected-card="currentCard"
        :selected-card-token="currentToken" />
    </template>
    <template #center>
      <CurrentCard
        v-if="presenceOfCard"
        :current-card="currentCard"
        :current-index="currentIndex"
        :current-token="currentToken"
        :errors-by-card="errorsByCard" />
    </template>
    <template #right>
      <RightPanel
        v-if="presenceOfCard"
        v-model="rightPane"
        class="max-h-[calc(100vh-100px)]"
        :panes="rightPanes"
        :counts="{ comments: reviewStore.openCount(currentToken) }">
        <div
          v-if="rightPane === 'settings'"
          class="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
          <SettingsGeneral />
        </div>
        <EditCardComments
          v-else
          :card="currentCard" />
      </RightPanel>
    </template>
  </ThreePanePage>
</template>
